import { CELL, type ContentLayout, type Glyph, type Kid } from './contentLayout';
import type { Theme } from './theme';

const FONT = 'Inter, system-ui, sans-serif';
const esc = (s: string): string => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** Einfache Symbole (24×24), werden in die Kacheln eingepasst. Linien in der Textfarbe. */
export const GLYPHS: Record<Glyph, (c: string) => string> = {
  person: (c) => `<circle cx="12" cy="8" r="4" fill="none" stroke="${c}" stroke-width="1.8"/><path d="M4 21c0-4.4 3.6-7 8-7s8 2.6 8 7" fill="none" stroke="${c}" stroke-width="1.8" stroke-linecap="round"/>`,
  place: (c) => `<path d="M12 22s7-6.2 7-12a7 7 0 1 0-14 0c0 5.8 7 12 7 12z" fill="none" stroke="${c}" stroke-width="1.8" stroke-linejoin="round"/><circle cx="12" cy="10" r="2.5" fill="none" stroke="${c}" stroke-width="1.8"/>`,
  object: (c) => `<path d="M12 3l8 4.5v9L12 21l-8-4.5v-9zM4 7.5l8 4.5 8-4.5M12 12v9" fill="none" stroke="${c}" stroke-width="1.8" stroke-linejoin="round"/>`,
  text: (c) => `<path d="M5 7h14M5 12h14M5 17h9" fill="none" stroke="${c}" stroke-width="1.8" stroke-linecap="round"/>`,
  event: (c) => `<circle cx="12" cy="12" r="6" fill="${c}"/>`,
  period: (c) => `<rect x="3.5" y="8" width="17" height="8" rx="2.5" fill="none" stroke="${c}" stroke-width="1.8"/>`,
  node: (c) => `<rect x="3.5" y="4.5" width="17" height="15" rx="2.5" fill="none" stroke="${c}" stroke-width="1.8"/><circle cx="9" cy="10" r="1.8" fill="${c}"/><path d="M5 18l5-5 3 3 2-2 4 4" fill="none" stroke="${c}" stroke-width="1.8" stroke-linejoin="round"/>`,
};

export function tile(kid: Kid, size: number, level: 1 | 2, theme: Theme, image: string | null, id: number): string {
  const border = level === 2 ? 6 : 5;
  const inner = size - 2 * border;
  const parts: string[] = [];
  parts.push(`<rect x="${border / 2}" y="${border / 2}" width="${size - border}" height="${size - border}" rx="${size * 0.16}" fill="${theme.surface}" stroke="${esc(kid.typeColor)}" stroke-width="${border}"/>`);
  if (image) {
    parts.push(`<clipPath id="k${id}"><rect x="${border}" y="${border}" width="${inner}" height="${inner}" rx="${size * 0.1}"/></clipPath>`);
    parts.push(`<image x="${border}" y="${border}" width="${inner}" height="${inner}" preserveAspectRatio="xMidYMid slice" clip-path="url(#k${id})" href="${esc(image)}" xlink:href="${esc(image)}"/>`);
  } else {
    const g = inner * 0.72;
    parts.push(`<g transform="translate(${(size - g) / 2} ${(size - g) / 2}) scale(${g / 24})">${GLYPHS[kid.glyph](theme.text)}</g>`);
  }
  if (kid.tagColor) parts.push(`<circle cx="${size - border - 2}" cy="${border + 2}" r="${Math.max(4, size * 0.08)}" fill="${esc(kid.tagColor)}" stroke="${theme.surface}" stroke-width="1.5"/>`);
  return parts.join('');
}

/**
 * Inhaltsvorschau eines Nodes als SVG: kleine farbige Punkte (fern), Kacheln mit Vorschaubild bzw. Symbol (mittel) oder größere
 * Symbole mit Namen (nah). `imageOf` liefert Titelbilder als Data-URI (SVG lädt keine externen Bilder).
 */
export function contentSvg(layout: ContentLayout, theme: Theme, imageOf: (assetId: string) => string | null): string {
  const { size, label } = CELL[layout.level];
  const p: string[] = [`<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="${layout.w}" height="${layout.h}" viewBox="0 0 ${layout.w} ${layout.h}">`];
  layout.items.forEach((it, i) => {
    p.push(`<g transform="translate(${it.x} ${it.y})">`);
    if (layout.level === 0) {
      p.push(`<rect width="${size}" height="${size}" rx="5" fill="${esc(it.kid.typeColor)}"/>`);
    } else {
      p.push(tile(it.kid, size, layout.level, theme, it.kid.imageId ? imageOf(it.kid.imageId) : null, i));
      if (label > 0) {
        const max = Math.floor(size / 6);
        const text = it.kid.title.length > max ? `${it.kid.title.slice(0, max - 1)}…` : it.kid.title;
        p.push(`<text x="${size / 2}" y="${size + 13}" text-anchor="middle" font-family="${FONT}" font-size="11" fill="${theme.text}">${esc(text)}</text>`);
      }
    }
    p.push('</g>');
  });
  p.push('</svg>');
  return p.join('');
}
